import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Tooltip,
  Icon,
  HStack,
  Box,
  Text,
  Collapse,
  useDisclosure,
  Divider,
  IconButton,
  Checkbox,
  CheckboxGroup,
  Stack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronDown, faChevronUp, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAdvertisers } from '../../hooks/useCampaigns';
import { useAdvertiserMutations } from '../../hooks/useAdvertisers';
import { useZones } from '../../hooks/useZones';
import AdvertiserModal from '../advertisers/AdvertiserModal';

// Helper component for labels with info tooltip
function LabelWithTooltip({ children, tooltip }) {
  return (
    <HStack spacing={1} display="inline-flex">
      <span>{children}</span>
      {tooltip && (
        <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
          <span>
            <Icon as={FontAwesomeIcon} icon={faInfoCircle} boxSize={3} color="gray.400" cursor="help" />
          </span>
        </Tooltip>
      )}
    </HStack>
  );
}

const initialFormData = {
  name: '',
  advertiserId: '',
  description: '',
  billingType: 'duration',
  budget: '',
  startDate: '',
  endDate: '',
  totalImpressionsLimit: '',
  dailyImpressionsLimit: '',
  priority: 5,
  weight: 1,
  zoneIds: [],
  // UTM Parameters
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

function CampaignModal({ isOpen, onClose, onSubmit, campaign, isLoading }) {
  const [formData, setFormData] = useState(initialFormData);
  const { data: advertisers, refetch: refetchAdvertisers } = useAdvertisers();
  const { createAdvertiser, loading: advertiserLoading } = useAdvertiserMutations();
  const { data: zones } = useZones();
  const toast = useToast();
  const { isOpen: isUtmOpen, onToggle: onUtmToggle } = useDisclosure();
  const { isOpen: isZonesOpen, onToggle: onZonesToggle } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isAdvertiserModalOpen, onOpen: onAdvertiserModalOpen, onClose: onAdvertiserModalClose } = useDisclosure();
  const isEditing = !!campaign;
  const zoneBg = useColorModeValue('gray.50', 'gray.700');

  const handleCreateAdvertiser = async (data) => {
    try {
      const result = await createAdvertiser(data);
      toast({
        title: 'Advertiser created',
        description: `${data.name} has been created successfully.`,
        status: 'success',
        duration: 3000,
      });
      await refetchAdvertisers();
      // Auto-select the newly created advertiser
      if (result?.data?.advertiser_id) {
        setFormData(prev => ({ ...prev, advertiserId: result.data.advertiser_id }));
      }
      onAdvertiserModalClose();
    } catch (err) {
      toast({
        title: 'Error creating advertiser',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.campaign_name || '',
        advertiserId: campaign.advertiser_id || '',
        description: campaign.description || '',
        billingType: campaign.billing_type || 'duration',
        budget: campaign.budget || '',
        startDate: campaign.start_date ? campaign.start_date.split('T')[0] : '',
        endDate: campaign.end_date ? campaign.end_date.split('T')[0] : '',
        totalImpressionsLimit: campaign.total_impressions_limit || '',
        dailyImpressionsLimit: campaign.daily_impressions_limit || '',
        priority: campaign.priority || 5,
        weight: campaign.weight || 1,
        zoneIds: campaign.zones?.map(z => z.zone_id) || campaign.zoneIds || [],
        // UTM Parameters
        utmSource: campaign.utm_source || '',
        utmMedium: campaign.utm_medium || '',
        utmCampaign: campaign.utm_campaign || '',
        utmTerm: campaign.utm_term || '',
        utmContent: campaign.utm_content || '',
      });
    } else {
      setFormData(initialFormData);
    }
  }, [campaign, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Campaign name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!formData.advertiserId) {
      toast({
        title: 'Please select an advertiser',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (formData.billingType === 'duration' && (!formData.startDate || !formData.endDate)) {
      toast({
        title: 'Start and end dates are required for duration-based campaigns',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const submitData = {
      name: formData.name,
      advertiserId: parseInt(formData.advertiserId),
      description: formData.description,
      billingType: formData.billingType,
      budget: formData.budget ? parseFloat(formData.budget) : null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      totalImpressionsLimit: formData.totalImpressionsLimit ? parseInt(formData.totalImpressionsLimit) : null,
      dailyImpressionsLimit: formData.dailyImpressionsLimit ? parseInt(formData.dailyImpressionsLimit) : null,
      priority: parseInt(formData.priority),
      weight: parseInt(formData.weight),
      zoneIds: formData.zoneIds.map(id => parseInt(id)),
      // UTM Parameters
      utmSource: formData.utmSource || null,
      utmMedium: formData.utmMedium || null,
      utmCampaign: formData.utmCampaign || null,
      utmTerm: formData.utmTerm || null,
      utmContent: formData.utmContent || null,
    };

    await onSubmit(submitData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEditing ? 'Edit Campaign' : 'New Campaign'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Left Column */}
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>
                    <LabelWithTooltip tooltip="A unique name to identify this campaign">
                      Campaign Name
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter campaign name"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    <LabelWithTooltip tooltip="The company or client running this campaign">
                      Advertiser
                    </LabelWithTooltip>
                  </FormLabel>
                  <HStack spacing={2}>
                    <Select
                      name="advertiserId"
                      value={formData.advertiserId}
                      onChange={handleChange}
                      placeholder="Select advertiser"
                      borderRadius="xl"
                      flex={1}
                    >
                      {advertisers?.map(adv => (
                        <option key={adv.advertiser_id} value={adv.advertiser_id}>
                          {adv.advertiser_name}
                        </option>
                      ))}
                    </Select>
                    <Tooltip label="Create new advertiser" hasArrow>
                      <IconButton
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        colorScheme="brand"
                        variant="outline"
                        borderRadius="xl"
                        onClick={onAdvertiserModalOpen}
                        aria-label="Create new advertiser"
                      />
                    </Tooltip>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Optional notes about this campaign">
                      Description
                    </LabelWithTooltip>
                  </FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Campaign description"
                    borderRadius="xl"
                    rows={3}
                  />
                </FormControl>

                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Duration: runs between dates. Impressions: stops after limit reached.">
                        Billing Type
                      </LabelWithTooltip>
                    </FormLabel>
                    <Select
                      name="billingType"
                      value={formData.billingType}
                      onChange={handleChange}
                      borderRadius="xl"
                    >
                      <option value="duration">Duration (Time-based)</option>
                      <option value="impressions">Impressions (Performance)</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Total budget allocated for this campaign">
                        Budget ($)
                      </LabelWithTooltip>
                    </FormLabel>
                    <NumberInput
                      value={formData.budget}
                      onChange={(val) => handleNumberChange('budget', val)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField borderRadius="xl" placeholder="0.00" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </VStack>

              {/* Right Column */}
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl isRequired={formData.billingType === 'duration'}>
                    <FormLabel>
                      <LabelWithTooltip tooltip="When the campaign starts running">
                        Start Date
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      borderRadius="xl"
                    />
                  </FormControl>
                  <FormControl isRequired={formData.billingType === 'duration'}>
                    <FormLabel>
                      <LabelWithTooltip tooltip="When the campaign stops running">
                        End Date
                      </LabelWithTooltip>
                    </FormLabel>
                    <Input
                      name="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={handleChange}
                      borderRadius="xl"
                    />
                  </FormControl>
                </SimpleGrid>

                {formData.billingType === 'impressions' && (
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>
                        <LabelWithTooltip tooltip="Maximum total impressions before campaign stops. Leave empty for unlimited.">
                          Total Impressions Limit
                        </LabelWithTooltip>
                      </FormLabel>
                      <NumberInput
                        value={formData.totalImpressionsLimit}
                        onChange={(val) => handleNumberChange('totalImpressionsLimit', val)}
                        min={0}
                      >
                        <NumberInputField borderRadius="xl" placeholder="Unlimited" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>
                        <LabelWithTooltip tooltip="Maximum impressions per day. Resets at midnight.">
                          Daily Impressions Limit
                        </LabelWithTooltip>
                      </FormLabel>
                      <NumberInput
                        value={formData.dailyImpressionsLimit}
                        onChange={(val) => handleNumberChange('dailyImpressionsLimit', val)}
                        min={0}
                      >
                        <NumberInputField borderRadius="xl" placeholder="Unlimited" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>
                )}

                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Higher priority campaigns are served first when multiple match (1-10)">
                        Priority (1-10)
                      </LabelWithTooltip>
                    </FormLabel>
                    <NumberInput
                      value={formData.priority}
                      onChange={(val) => handleNumberChange('priority', val)}
                      min={1}
                      max={10}
                    >
                      <NumberInputField borderRadius="xl" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      <LabelWithTooltip tooltip="Among same-priority campaigns, higher weight = more likely to be shown">
                        Weight
                      </LabelWithTooltip>
                    </FormLabel>
                    <NumberInput
                      value={formData.weight}
                      onChange={(val) => handleNumberChange('weight', val)}
                      min={1}
                      max={100}
                    >
                      <NumberInputField borderRadius="xl" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </SimpleGrid>

            {/* Zone Selection Section */}
            <Box mt={6}>
              <Divider mb={4} />
              <Button
                variant="ghost"
                onClick={onZonesToggle}
                width="100%"
                justifyContent="space-between"
                rightIcon={<FontAwesomeIcon icon={isZonesOpen ? faChevronUp : faChevronDown} />}
                mb={2}
              >
                <HStack>
                  <Text fontWeight="500">Target Zones</Text>
                  {formData.zoneIds.length > 0 && (
                    <Badge colorScheme="green" borderRadius="full">{formData.zoneIds.length} selected</Badge>
                  )}
                </HStack>
              </Button>
              <Collapse in={isZonesOpen}>
                <Box p={4} bg={zoneBg} borderRadius="xl">
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Select which ad zones this campaign can display in. Ads will only show in zones that match their dimensions.
                  </Text>
                  {zones && zones.length > 0 ? (
                    <CheckboxGroup
                      value={formData.zoneIds.map(String)}
                      onChange={(values) => setFormData(prev => ({ ...prev, zoneIds: values.map(Number) }))}
                    >
                      <Stack spacing={2} maxH="200px" overflowY="auto">
                        {zones.map(zone => (
                          <Checkbox
                            key={zone.zone_id}
                            value={String(zone.zone_id)}
                            colorScheme="brand"
                          >
                            <HStack spacing={2}>
                              <Text>{zone.zone_name}</Text>
                              <Badge size="sm" colorScheme="gray" fontSize="xs">
                                {zone.width}x{zone.height}
                              </Badge>
                              <Badge size="sm" colorScheme={zone.zone_type === 'email' ? 'purple' : 'blue'} fontSize="xs">
                                {zone.zone_type}
                              </Badge>
                            </HStack>
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      No zones available. Create zones first to link them to campaigns.
                    </Text>
                  )}
                </Box>
              </Collapse>
            </Box>

            {/* UTM Parameters Section */}
            <Box mt={6}>
              <Divider mb={4} />
              <Button
                variant="ghost"
                onClick={onUtmToggle}
                width="100%"
                justifyContent="space-between"
                rightIcon={<FontAwesomeIcon icon={isUtmOpen ? faChevronUp : faChevronDown} />}
                mb={2}
              >
                <HStack>
                  <Text fontWeight="500">UTM Parameters</Text>
                  <Text fontSize="sm" color="gray.500">(Optional)</Text>
                </HStack>
              </Button>
              <Collapse in={isUtmOpen}>
                <Box p={4} bg="gray.50" borderRadius="xl" _dark={{ bg: 'gray.700' }}>
                  <Text fontSize="sm" color="gray.500" mb={4}>
                    Set default UTM parameters for all ads in this campaign. Individual ads can override these values.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies which site sent the traffic (e.g., google, newsletter, twitter)">
                          utm_source
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmSource"
                        value={formData.utmSource}
                        onChange={handleChange}
                        placeholder="e.g., statfalcon"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies what type of link was used (e.g., banner, email, cpc)">
                          utm_medium
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmMedium"
                        value={formData.utmMedium}
                        onChange={handleChange}
                        placeholder="e.g., banner"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies a specific campaign or promotion (e.g., summer_sale)">
                          utm_campaign
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmCampaign"
                        value={formData.utmCampaign}
                        onChange={handleChange}
                        placeholder="e.g., summer_sale_2024"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Identifies paid search keywords (optional)">
                          utm_term
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmTerm"
                        value={formData.utmTerm}
                        onChange={handleChange}
                        placeholder="e.g., running+shoes"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                      <FormLabel fontSize="sm">
                        <LabelWithTooltip tooltip="Differentiates similar content or links (e.g., banner_v1, header_cta)">
                          utm_content
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="utmContent"
                        value={formData.utmContent}
                        onChange={handleChange}
                        placeholder="e.g., hero_banner"
                        size="sm"
                        borderRadius="lg"
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>
              </Collapse>
            </Box>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isLoading}
              borderRadius="xl"
            >
              {isEditing ? 'Save Changes' : 'Create Campaign'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>

      {/* Nested Advertiser Modal */}
      <AdvertiserModal
        isOpen={isAdvertiserModalOpen}
        onClose={onAdvertiserModalClose}
        onSubmit={handleCreateAdvertiser}
        isLoading={advertiserLoading}
      />
    </Modal>
  );
}

export default CampaignModal;
